import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { TrainingSample } from '../../types';

interface DeleteTrainingSampleDialogProps {
  open: boolean;
  sample: TrainingSample | null;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export const DeleteTrainingSampleDialog: React.FC<DeleteTrainingSampleDialogProps> = ({
  open,
  sample,
  onClose,
  onConfirm,
  isDeleting = false,
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        Delete Training Sample
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to delete this training sample? This action cannot be undone.
        </DialogContentText>
        
        {sample && (
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Sample Details:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>ID:</strong> #{sample.id.slice(-8)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Prediction:</strong> {sample.prediction || 'No prediction'}
            </Typography>
            {sample.confidence !== undefined && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Confidence:</strong> {(sample.confidence * 100).toFixed(1)}%
              </Typography>
            )}
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={sample.review_status} 
                size="small" 
                sx={{ mr: 1 }}
              />
              {sample.is_validated && (
                <Chip 
                  label="Validated" 
                  size="small" 
                  color="success" 
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
              )}
              {sample.use_for_training && (
                <Chip 
                  label="Training Data" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained"
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete Sample'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};